import { TableFeature } from '../core/instance'
import {
  OnChangeFn,
  TableGenerics,
  TableInstance,
  RowModel,
  Updater,
} from '../types'
import { functionalUpdate, makeStateUpdater, memo } from '../utils'

export type PaginationState = {
  pageIndex: number
  pageSize: number
}

export type PaginationTableState = {
  pagination: PaginationState
}

export type PaginationInitialTableState = {
  pagination?: Partial<PaginationState>
}

export type PaginationOptions<TGenerics extends TableGenerics> = {
  pageCount?: number
  manualPagination?: boolean
  onPaginationChange?: OnChangeFn<PaginationState>
  autoResetPageIndex?: boolean
  getPaginationRowModel?: (instance: TableInstance<any>) => () => RowModel<any>
}

export type PaginationDefaultOptions = {
  onPaginationChange: OnChangeFn<PaginationState>
}

export type PaginationInstance<TGenerics extends TableGenerics> = {
  _autoResetPageIndex: () => void
  setPagination: (updater: Updater<PaginationState>) => void
  resetPagination: (defaultState?: boolean) => void
  setPageIndex: (updater: Updater<number>) => void
  resetPageIndex: (defaultState?: boolean) => void
  setPageSize: (updater: Updater<number>) => void
  resetPageSize: (defaultState?: boolean) => void
  setPageCount: (updater: Updater<number>) => void
  getPageOptions: () => number[]
  getCanPreviousPage: () => boolean
  getCanNextPage: () => boolean
  previousPage: () => void
  nextPage: () => void
  getPrePaginationRowModel: () => RowModel<TGenerics>
  getPaginationRowModel: () => RowModel<TGenerics>
  _getPaginationRowModel?: () => RowModel<TGenerics>
  getPageCount: () => number
}

//

const defaultPageIndex = 0
const defaultPageSize = 10

const getDefaultPaginationState = (): PaginationState => ({
  pageIndex: defaultPageIndex,
  pageSize: defaultPageSize,
})

export const Pagination: TableFeature = {
  getInitialState: (state): PaginationTableState => {
    return {
      ...state,
      pagination: {
        ...getDefaultPaginationState(),
        ...state?.pagination,
      },
    }
  },

  getDefaultOptions: <TGenerics extends TableGenerics>(
    instance: TableInstance<TGenerics>
  ): PaginationDefaultOptions => {
    return {
      onPaginationChange: makeStateUpdater('pagination', instance),
    }
  },

  createInstance: <TGenerics extends TableGenerics>(
    instance: TableInstance<TGenerics>
  ): PaginationInstance<TGenerics> => {
    let registered = false
    let queued = false

    return {
      _autoResetPageIndex: () => {
        if (!registered) {
          instance._queue(() => {
            registered = true
          })
          return
        }

        if (
          instance.options.autoResetAll ??
          instance.options.autoResetPageIndex ??
          !instance.options.manualPagination
        ) {
          if (queued) return
          queued = true
          instance._queue(() => {
            instance.resetPageIndex()
            queued = false
          })
        }
      },
      setPagination: updater => {
        const safeUpdater: Updater<PaginationState> = old => {
          let newState = functionalUpdate(updater, old)

          return newState
        }

        return instance.options.onPaginationChange?.(safeUpdater)
      },
      resetPagination: defaultState => {
        instance.setPagination(
          defaultState
            ? getDefaultPaginationState()
            : instance.initialState.pagination ?? getDefaultPaginationState()
        )
      },
      setPageIndex: updater => {
        instance.setPagination(old => {
          let pageIndex = functionalUpdate(updater, old.pageIndex)

          const maxPageIndex =
            (typeof instance.options.pageCount === 'undefined' || instance.options.pageCount === -1) ?
              Number.MAX_SAFE_INTEGER
              : instance.options.pageCount - 1

          pageIndex = Math.min(Math.max(0, pageIndex), maxPageIndex)

          return {
            ...old,
            pageIndex,
          }
        })
      },
      resetPageIndex: defaultState => {
        instance.setPageIndex(
          defaultState
            ? defaultPageIndex
            : instance.initialState?.pagination?.pageIndex ?? defaultPageIndex
        )
      },
      resetPageSize: defaultState => {
        instance.setPageSize(
          defaultState
            ? defaultPageSize
            : instance.initialState?.pagination?.pageSize ?? defaultPageSize
        )
      },
      setPageSize: updater => {
        instance.setPagination(old => {
          const pageSize = Math.max(1, functionalUpdate(updater, old.pageSize))
          const topRowIndex = old.pageSize * old.pageIndex!
          const pageIndex = Math.floor(topRowIndex / pageSize)

          return {
            ...old,
            pageIndex,
            pageSize,
          }
        })
      },
      setPageCount: updater =>
        instance.setPagination(old => {
          let newPageCount = functionalUpdate(
            updater,
            instance.options.pageCount ?? -1
          )

          if (typeof newPageCount === 'number') {
            newPageCount = Math.max(-1, newPageCount)
          }

          return {
            ...old,
            pageCount: newPageCount,
          }
        }),

      getPageOptions: memo(
        () => [instance.getPageCount()],
        pageCount => {
          let pageOptions: number[] = []
          if (pageCount && pageCount > 0) {
            pageOptions = [...new Array(pageCount)].fill(null).map((_, i) => i)
          }
          return pageOptions
        },
        {
          key: process.env.NODE_ENV === 'development' && 'getPageOptions',
          debug: () => instance.options.debugAll ?? instance.options.debugTable,
        }
      ),

      getCanPreviousPage: () => instance.getState().pagination.pageIndex > 0,

      getCanNextPage: () => {
        const { pageIndex } = instance.getState().pagination

        const pageCount = instance.getPageCount()

        if (pageCount === -1) {
          return true
        }

        if (pageCount === 0) {
          return false
        }

        return pageIndex < pageCount - 1
      },

      previousPage: () => {
        return instance.setPageIndex(old => old - 1)
      },

      nextPage: () => {
        return instance.setPageIndex(old => {
          return old + 1
        })
      },

      getPrePaginationRowModel: () => instance.getExpandedRowModel(),
      getPaginationRowModel: () => {
        if (
          !instance._getPaginationRowModel &&
          instance.options.getPaginationRowModel
        ) {
          instance._getPaginationRowModel =
            instance.options.getPaginationRowModel(instance)
        }

        if (
          instance.options.manualPagination ||
          !instance._getPaginationRowModel
        ) {
          return instance.getPrePaginationRowModel()
        }

        return instance._getPaginationRowModel()
      },

      getPageCount: () => {
        return (
          instance.options.pageCount ??
          Math.ceil(
            instance.getPrePaginationRowModel().rows.length /
            instance.getState().pagination.pageSize
          )
        )
      },
    }
  },
}
